import React, { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { saveAs } from 'file-saver'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory, useParams } from 'react-router-dom'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import MeetingParticipantsForm from './MeetingParticipantsForm'
import MeetingMinuteForm from './MeetingMinuteForm'
import MyButton from '../../../control/button/button'
import useLoading from '../../../control/loading/useLoading'
import { useSettingAgendas } from '../../../services/um/useSettingAgendas'
import { buildAgendaPayload, formatYmd, mapAgendaResponseToForm, hasAtLeastOneAttendance } from './utils'
import type { AgendaEditorForm } from './agendaTypes'
import useTranslate from '../../../i18n/useTranslate'
import Dialog, { DialogType } from '../../../control/dialog/dialog'
import ApproveAgendaForm, { ApprovalStatusId } from './ApproveAgendaForm'
import SecretCodeForm from '../../web/shareUI/secretCodeForm/secretCodeForm'
import { encryption } from '../../../utilities/encryption'
import useAuthenStore from '../../../stores/authen/authenStore'
import userGroup from '../../../constants/userGroup'

type EditMinutesTabProps = {
    agendaReportId?: number
    canShow?: boolean
    onComplete?: () => void
}

const ALLOWED_SIGN_GROUPS = new Set<number>([userGroup.president, userGroup.officer])

const EditMinutesTab: React.FC<EditMinutesTabProps> = ({ agendaReportId, canShow, onComplete }) => {
    const classes = useStyles()
    const loading = useLoading()
    const history = useHistory()
    const t = useTranslate()
    const profileStore = useAuthenStore()

    const { id } = useParams<{ id: string }>()
    const {
        getAgendaReportById,
        upsertAgendaReport,
        sendMailReport,
        approveAgendaReport,
        getAgendaReportFileArrayBuffer,
    } = useSettingAgendas()

    const [initialValues, setInitialValues] = useState<Partial<AgendaEditorForm> | null>(null)
    const [apiReportData, setApiReportData] = useState<any | null>(null)
    const [rev, setRev] = useState(0)

    const submitIntentRef = useRef<'draft' | 'submit'>('draft')

    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = useRef<Record<string, Function>>()
    const onRefDialog = (fn: Record<string, Function>) => {
        dialogRef.current = fn
    }

    useEffect(() => {
        if (canShow === false) return

        let alive = true
        ;(async () => {
            loading.loadingOpen()
            try {
                const res = await getAgendaReportById(
                    String(profileStore?.profile?.irb),
                    id,
                    agendaReportId ? String(agendaReportId) : undefined,
                )
                const data = (res?.result ?? res?.data ?? res) as any
                const mappedForm = mapAgendaResponseToForm(data)
                if (alive) {
                    setInitialValues(mappedForm)
                    setApiReportData(data)
                }
            } finally {
                loading.loadingClose()
            }
        })()
        return () => {
            alive = false
        }
    }, [id, agendaReportId, canShow])

    const refreshReport = async () => {
        const res = await getAgendaReportById(
            String(profileStore?.profile?.irb),
            id,
            agendaReportId ? String(agendaReportId) : undefined,
        )
        const data = (res?.result ?? res?.data ?? res) as any
        const mappedForm = mapAgendaResponseToForm(data)
        setInitialValues(mappedForm)
        setApiReportData(data)
        setRev((r) => r + 1)
    }

    const formKey = useMemo(() => `minutes-init-${id}-${agendaReportId ?? 'na'}-${rev}`, [id, agendaReportId, rev])

    const confirmFormSubmit = (intent: 'draft' | 'submit', form: any) => {
        if (intent === 'draft') {
            submitIntentRef.current = 'draft'
            void onSubmit(form.getState().values)
            return
        }

        if (!hasAtLeastOneAttendance(form.getState().values)) {
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('UM.SETTING_AGENDA.PARTICIPANTS.REQUIRE_ATTENDANCE'),
                type: 'alert-message',
            })
            dialogRef.current?.openDialog()
            return
        }

        setConfigDialog({
            title: t.get('ALERT.TITLE.NOTIFICATION'),
            content: t.get('ALERT.CONTENT.NEED_TO_SAVE'),
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                submitIntentRef.current = 'submit'
                form.submit()
            },
            onAfterClose: () => setConfigDialog(undefined),
        })
        dialogRef.current?.openDialog()
    }

    const onSubmit = async (values: any) => {
        loading.loadingOpen()
        try {
            const isDraft = submitIntentRef.current === 'draft'
            const payload = { ...buildAgendaPayload(values), agendaReportId, isDraft }
            await upsertAgendaReport(String(profileStore?.profile?.irb), payload)
            await refreshReport()
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
            })
            dialogRef.current?.openDialog()
            onComplete?.()
        } finally {
            loading.loadingClose()
        }
    }

    const onConfirmSendEmail = async () => {
        if (!id) return
        loading.loadingOpen()
        try {
            await sendMailReport(String(profileStore?.profile?.irb), id, agendaReportId ? String(agendaReportId) : '')
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.SEND_EMAIL_SUCCESS'),
                type: 'alert-message',
            })
        } finally {
            loading.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    const handleSendEmail = () => {
        if (!id) return
        setConfigDialog({
            title: t.get('ALERT.TITLE.NOTIFICATION'),
            content: t.get('ALERT.CONTENT.NEED_TO_SEND_EMAIL'),
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                void onConfirmSendEmail()
            },
            onAfterClose: () => setConfigDialog(undefined),
        })
        dialogRef.current?.openDialog()
    }

    const onConfirmApproveMinutes = async (values: any, plainPin?: string) => {
        if (!id) return
        loading.loadingOpen()
        try {
            const status = String(values?.approvalStatusId)
            const isApprove = status === ApprovalStatusId.approved

            const payload = {
                isApprove,
                ...(isApprove && plainPin
                    ? { signUserPinCode: encryption(plainPin), signUserID: String(profileStore.profile.userId) }
                    : {}),
                ...(!isApprove && (values?.remark ?? '').trim() ? { remark: (values.remark as string).trim() } : {}),
            }

            const res = await approveAgendaReport(
                String(profileStore?.profile?.irb),
                id,
                agendaReportId ? String(agendaReportId) : '',
                payload,
            )
            if (res.status === 1) {
                await refreshReport()
                setConfigDialog({
                    title: t.get('ALERT.TITLE.NOTIFICATION'),
                    content: t.get('ALERT.CONTENT.SAVE_SUCCESS'),
                    type: 'alert-message',
                })
            } else {
                setConfigDialog({
                    title: t.get('ALERT.TITLE.NOTIFICATION'),
                    content: t.get('ALERT.CONTENT.SECRET_PASSWORD_INCORRECT'),
                    type: 'alert-message',
                })
            }
        } finally {
            loading.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    const handleApproveMinutes = (values: any) => {
        const isApprove = values.approvalStatusId === ApprovalStatusId.approved

        if (!isApprove) {
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.NEED_TO_SAVE'),
                type: 'alert-confirm',
                onClickConfirm: () => {
                    dialogRef.current?.closeDialog()
                    setTimeout(() => onConfirmApproveMinutes(values), 0)
                },
                onAfterClose: () => setConfigDialog(undefined),
            })
            dialogRef.current?.openDialog()
            return
        }

        setConfigDialog({
            title: t.get('LABEL.SECRET_PASSWORD'),
            content: (
                <SecretCodeForm
                    onClickClose={() => dialogRef.current?.closeDialog()}
                    onClickSave={(val: any) => {
                        dialogRef.current?.closeDialog()
                        onConfirmApproveMinutes(values, val.password)
                    }}
                />
            ),
            type: '',
        })
        dialogRef.current?.openDialog()
    }

    const isTh = useMemo(() => t.languageStore.currentLanguage === t.languageKey.th, [t.languageStore.currentLanguage])

    const handleExportReportPdfUnsigned = async () => {
        if (!id || !agendaReportId) return
        loading.loadingOpen()
        try {
            const irb = String(profileStore?.profile?.irb)
            const reportId = String(agendaReportId)
            const fileName = `${`${t.get('UM.SETTING_AGENDA.DOCUMENT.AGENDA_SUMMARY')}_${formatYmd(new Date())}`}`
            const keyName = isTh ? 'unSignReportFileName_th' : 'unSignReportFileName_en'
            const res: ArrayBuffer = await getAgendaReportFileArrayBuffer(irb, id, reportId, 'pdf', keyName, fileName)
            const blob = new Blob([res], { type: 'application/pdf' })
            saveAs(blob, fileName)
        } finally {
            loading.loadingClose()
        }
    }

    const handleExportReportPdfSigned = async () => {
        if (!id || !agendaReportId) return
        loading.loadingOpen()
        try {
            const irb = String(profileStore?.profile?.irb)
            const reportId = String(agendaReportId)
            const fileName = `${`${t.get('UM.SETTING_AGENDA.DOCUMENT.AGENDA_SUMMARY_SIGNED')}_${formatYmd(new Date())}`}`
            const keyName = isTh ? 'signReportFileName_th' : 'signReportFileName_en'
            const res: ArrayBuffer = await getAgendaReportFileArrayBuffer(irb, id, reportId, 'pdf', keyName, fileName)
            const blob = new Blob([res], { type: 'application/pdf' })
            saveAs(blob, fileName)
        } finally {
            loading.loadingClose()
        }
    }

    const handleExportReportDocxUnsigned = async () => {
        if (!id || !agendaReportId) return
        loading.loadingOpen()
        try {
            const irb = String(profileStore?.profile?.irb)
            const reportId = String(agendaReportId)
            const fileName = `${`${t.get('UM.SETTING_AGENDA.DOCUMENT.AGENDA_SUMMARY')}_${formatYmd(new Date())}`}`
            const keyName = isTh ? 'unSignReportFileName_th' : 'unSignReportFileName_en'
            const res: ArrayBuffer = await getAgendaReportFileArrayBuffer(irb, id, reportId, 'docx', keyName, fileName)
            const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            })
            saveAs(blob, fileName)
        } finally {
            loading.loadingClose()
        }
    }

    if (canShow === false) {
        return (
            <Box className={classes.emptyWrap}>
                <Typography variant="body1">{t.get('UM.SETTING_AGENDA.CANNOT_CREATE_REPORT')}</Typography>
            </Box>
        )
    }

    if (!initialValues) return null

    const canSign = Number(initialValues?.submittedStatusId) === 2
    const canEdit = [1, 11].includes(apiReportData?.submittedStatusId) || !apiReportData?.agendaReportId
    const userGroupId = profileStore?.profile?.userGroupId ?? 0
    const canSignByRole = ALLOWED_SIGN_GROUPS.has(userGroupId)
    const isSignDisabled = !canSignByRole
    const canRejected = [11].includes(apiReportData?.submittedStatusId)

    return (
        <>
            <FinalForm
                key={formKey}
                initialValues={initialValues}
                mutators={{ ...arrayMutators }}
                onSubmit={onSubmit}
                validate={() => {
                    return {}
                }}
                render={({ form }) => (
                    <Box component="form" display="flex" flexDirection="column" style={{ gap: 20 }}>
                        <MeetingParticipantsForm
                            prefix="participants"
                            showAgendaSummary
                            showExportButton
                            showLeavers
                            isSigned={apiReportData?.submittedStatusId === 8}
                            onExportSummaryPdf={handleExportReportPdfUnsigned}
                            onExportSummaryPdfSigned={handleExportReportPdfSigned}
                            onExportSummaryDocx={handleExportReportDocxUnsigned}
                            canEdit={canEdit}
                        />
                        <MeetingMinuteForm prefix="topics" canEdit={canEdit} />
                        {canRejected && <ApproveAgendaForm canEdit={false} />}
                        {!canSign && (
                            <Box className={classes.pageFooter}>
                                {canEdit ? (
                                    <>
                                        <MyButton
                                            className={classes.footerButton}
                                            variant="contained"
                                            onClick={() => confirmFormSubmit('draft', form)}
                                        >
                                            {t.get('UM.SETTING_AGENDA.BUTTON.SAVE_DRAFT')}
                                        </MyButton>

                                        <div className={classes.rightActions}>
                                            <MyButton
                                                className={classes.footerButton}
                                                variant="outlined"
                                                onClick={() => {
                                                    history.push('/settingAgendas')
                                                }}
                                            >
                                                {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                            </MyButton>

                                            <MyButton
                                                className={classes.footerButton}
                                                variant="contained"
                                                color="primary"
                                                onClick={() => confirmFormSubmit('submit', form)}
                                            >
                                                {t.get('UM.SETTING_AGENDA.BUTTON.SAVE_AND_REQUEST_APPROVAL')}
                                            </MyButton>
                                        </div>
                                    </>
                                ) : (
                                    <div className={classes.rightActions}>
                                        <MyButton
                                            className={classes.footerButton}
                                            variant="outlined"
                                            onClick={() => {
                                                history.push('/settingAgendas')
                                            }}
                                        >
                                            {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                        </MyButton>
                                        <MyButton
                                            className={clsx(classes.footerButton, classes.sendEmailButton)}
                                            variant="contained"
                                            onClick={handleSendEmail}
                                        >
                                            {t.get('UM.SETTING_AGENDA.BUTTON.SEND_EMAIL')}
                                        </MyButton>
                                    </div>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            />

            {canSign && (
                <FinalForm
                    key={`minutes-sign-form-${id}-${agendaReportId ?? 'na'}-${rev}`}
                    initialValues={initialValues}
                    mutators={{ ...arrayMutators }}
                    onSubmit={handleApproveMinutes}
                    validate={() => {
                        return {}
                    }}
                    render={({ handleSubmit }) => (
                        <>
                            <ApproveAgendaForm />
                            <Box className={classes.pageFooter}>
                                <div className={classes.rightActions}>
                                    <MyButton
                                        className={classes.footerButton}
                                        variant="outlined"
                                        onClick={() => {
                                            history.push('/settingAgendas')
                                        }}
                                    >
                                        {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                    </MyButton>
                                    <MyButton
                                        variant="contained"
                                        color="primary"
                                        disabled={isSignDisabled}
                                        onClick={handleSubmit}
                                    >
                                        {/* {t.get('UM.SETTING_AGENDA.BUTTON.SIGN')} */}
                                        {t.get('BUTTON.SAVE_2')}
                                    </MyButton>
                                </div>
                            </Box>
                        </>
                    )}
                />
            )}

            <Dialog onRef={onRefDialog} {...configDialog} />
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    sendEmailButton: {
        backgroundColor: '#0062FF',
        color: '#fff',
        transition: 'background-color .2s ease, box-shadow .2s ease',
        '&&:hover': { backgroundColor: '#004DE6', boxShadow: '0 2px 8px rgba(0,0,0,.15)' },
        '&&:active': { backgroundColor: '#003FC7', boxShadow: '0 1px 4px rgba(0,0,0,.12)' },
        '&&.Mui-disabled': { backgroundColor: '#B3D1FF', color: '#fff' },
    },
    emptyWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 250,
        borderRadius: 12,
        border: '1px solid rgba(0,0,0,0.08)',
    },
    pageFooter: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    rightActions: {
        marginLeft: 'auto',
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            flexDirection: 'column',
        },
    },
    footerButton: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}))

export default EditMinutesTab
